import { useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeftIcon } from '@phosphor-icons/react/dist/csr/ArrowLeft';
import { PasswordIcon } from '@phosphor-icons/react/dist/csr/Password';

import { Button } from '../../design-system/primitives/Button/Button';
import { Input } from '../../design-system/primitives/Input/Input';
import {
  ApiError,
  NETWORK_ERROR_CODE,
  PAGE_EXPIRED_CODE,
  TOO_MANY_REQUESTS_CODE,
  api,
} from '../../lib/api/client';
import styles from './ResetPasswordScreen.module.css';

/**
 * Redeems a `POST /auth/password/forgot` code — `POST /auth/password/reset` —
 * the second of the two signed-out account-recovery screens. The first, which
 * only asks for an address and never says whether it exists, is task 6's
 * `ForgotPasswordScreen`.
 *
 * It renders OUTSIDE `AppShell`, the same way `LoginPage`, `ChangePasswordScreen`
 * and `ForgotPasswordScreen` do: nobody reaching this screen has a session,
 * so there is no nav to render and no shell to put it in. It owns its own
 * `<h1>` for the same reason those screens do.
 *
 * ## Reached two ways — decision R-8
 *
 * `App.tsx`'s anonymous branch mounts this screen from two different places:
 *
 * - from `ForgotPasswordScreen`'s confirmation, with `email` pre-filled —
 *   the person just asked for a code and is continuing straight on;
 * - from `LoginPage`'s "I already have a code" control, with `email` EMPTY —
 *   the code lives in a mailbox for sixty minutes and people close tabs
 *   between requesting a code and opening the mail. If the only door in were
 *   through the request form, reaching this screen with a code already in
 *   hand would mean requesting a second one first, which invalidates the
 *   first (`password_reset_tokens` holds one row per address). `email` is
 *   only ever a STARTING value — it is never re-read from the prop after the
 *   first render, exactly like `ChangePasswordScreen`'s fields are never
 *   re-seeded from a prop after mount.
 *
 * ## The code is VISIBLE, like every other OTP/recovery code in this codebase
 *
 * `resetPasswordCode` is `type="text"`, not `type="password"` — the house
 * precedent, and it stays that way after review (task 7, Important 2).
 * `ChallengeForm.tsx` leaves both the authenticator OTP and the printed
 * recovery code visible for the identical reason: a person transcribing a
 * six-to-several-character code off an email or a piece of paper who
 * mistypes one character needs to SEE what they typed, or an "invalid or
 * expired" response (the one message `PasswordResetController::failure()`
 * gives for every rejection) reads as "the code itself was wrong" when it
 * was a typo the person could have caught by looking. Masking it also fights
 * the platform: `type="password"` sitting beside an `type="email"` field is
 * the canonical login shape, so a password manager routinely ignores
 * `autoComplete="off"` here and offers to fill the ACCOUNT'S SAVED PASSWORD
 * into the code field, or to "update your saved password" with the code
 * itself — actively wrong autofill on the one field where it matters most.
 * `autoComplete="one-time-code"` names what the field actually is, for
 * whatever platform affordance exists for that.
 *
 * It is still in `KNOWN_SECRET_FIELD_IDS` and still stripped by the DOM-leak
 * test below — that check selects by ID, never by `type`, precisely so a
 * regression could not silently exempt this field by changing its `type`.
 *
 * ## The confirmation check — client-side AND server-side, for real this time
 *
 * `newPassword !== confirmPassword` is checked here before anything is sent,
 * so the person is told immediately. Unlike `ChangePasswordScreen` (whose
 * `api.changePassword` still synthesises `password_confirmation` from
 * `password` itself — a pre-existing gap, out of this task's scope),
 * `api.resetPassword` takes `password` and `passwordConfirmation` as two
 * SEPARATE arguments and sends them as given (see the client's own
 * docblock). That is what makes this screen's client-side check
 * defence-in-depth rather than the ONLY confirmation control in the system:
 * Laravel's `confirmed` rule on `ResetPasswordRequest` now compares two
 * independently-transmitted wire values, so a client that shipped with this
 * check accidentally removed would still be caught server-side, on a 422
 * under the `password` field. No client-side length or composition rule is
 * enforced beyond "not blank": the server's `min:12` is the one that
 * actually gates what the new password may be, exactly as
 * `ChangePasswordScreen`'s docblock explains for its own fields.
 *
 * ## `onReset` fires on SUCCESS, not on a click — and that is correct here
 *
 * This is the opposite rule from `ForgotPasswordScreen`'s `onRequested`, and
 * deliberately so: that screen's confirmation is UNCONDITIONAL by design (an
 * enumeration defence — see its docblock), so a click is the only signal
 * that means anything. This screen has no such defence to preserve: a wrong
 * or expired code, an address the code was not issued for, and a password
 * that fails the server's rules are three REAL, DISTINCT failures the person
 * needs to see, and `api.resetPassword` resolving is the one true signal
 * that the account's password actually changed. `onReset` is called once,
 * from the `try` block's success path, the same shape
 * `ChangePasswordScreen`'s `onChanged` already uses for the identical reason.
 *
 * ## No session comes back — R-2
 *
 * `api.resetPassword` resolves to `{ status: 'reset' }` and nothing else:
 * the API deliberately mints no session and no token from a mailed code, so
 * an account enrolled in multi-factor authentication still meets its
 * challenge at the ordinary sign-in screen afterwards. `onReset` therefore
 * takes no user resource — there is none to hand up — and the caller's job
 * is only to return to `LoginPage` and say what happened.
 */

export interface ResetPasswordScreenProps {
  /**
   * The address to start the form with. Non-empty when reached from
   * `ForgotPasswordScreen`'s confirmation; empty when reached from
   * `LoginPage`'s "I already have a code" control. Read once, into this
   * component's own state — never re-applied after the first render.
   */
  email: string;
  /**
   * Called once `api.resetPassword` has actually resolved. See the module
   * docblock's "R-2" section: there is no session and no user resource to
   * hand up, only the fact that it happened.
   */
  onReset: () => void;
  /** Returns to sign-in without submitting anything. */
  onCancel: () => void;
}

type FieldKey = 'email' | 'code' | 'new' | 'confirm';

/** The server's three validated fields, mapped onto this screen's four
 *  inputs. There is no server-side field for `confirm` — it is never sent
 *  (see the module docblock) — so a `password` failure (too short) is shown
 *  under the NEW password input, the field it is actually about. */
const FIELD_MAP: Readonly<Record<string, FieldKey>> = {
  email: 'email',
  code: 'code',
  password: 'new',
};

/** First message per field, matching `ChangePasswordScreen.tsx`'s `mapDetails`. */
function mapDetails(details: Readonly<Record<string, string[]>>): Partial<Record<FieldKey, string>> {
  const out: Partial<Record<FieldKey, string>> = {};
  for (const [field, messages] of Object.entries(details)) {
    const key = FIELD_MAP[field];
    if (key === undefined) continue;
    const [first] = messages;
    if (first !== undefined) out[key] = first;
  }
  return out;
}

export function ResetPasswordScreen({ email: initialEmail, onReset, onCancel }: ResetPasswordScreenProps) {
  const { t } = useTranslation();

  const [email, setEmail] = useState(initialEmail);
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const [missing, setMissing] = useState<readonly FieldKey[]>([]);
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<FieldKey, string>>>({});
  const [generalFailure, setGeneralFailure] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  /** Read inside the handler: two clicks in one React batch both see `false`. */
  const inFlight = useRef(false);

  /**
   * Whole-branch review, MUST-FIX 3. The four `Field` error paragraphs are
   * `role="alert"` regions born WITH their content — `reliable: false` in
   * `src/test/announcements.ts`'s own model — and on an empty submit
   * NOTHING else was set: no `generalFailure`, no moved focus. A
   * screen-reader user got silence from a submission that visibly did
   * nothing. `LoginPage.tsx` and `ForgotPasswordScreen.tsx` both compensate
   * for the identical shape by setting the real alert region AND moving
   * focus to the first empty field; this screen now does the same.
   */
  const emailRef = useRef<HTMLInputElement>(null);
  const codeRef = useRef<HTMLInputElement>(null);
  const newPasswordRef = useRef<HTMLInputElement>(null);
  const confirmPasswordRef = useRef<HTMLInputElement>(null);
  const fieldRefs: Readonly<Record<FieldKey, typeof emailRef>> = {
    email: emailRef,
    code: codeRef,
    new: newPasswordRef,
    confirm: confirmPasswordRef,
  };

  function describeFailure(error: ApiError): string {
    switch (error.code) {
      case TOO_MANY_REQUESTS_CODE:
        return t('auth.throttled');
      case PAGE_EXPIRED_CODE:
        return t('auth.sessionExpired');
      case NETWORK_ERROR_CODE:
        return t('auth.unreachable');
      default:
        return t('resetPassword.failed');
    }
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    // FIRST statement: everything below can throw, and a default-action
    // submit on a form with no method is a GET that would put the address,
    // the code and both passwords in the URL.
    event.preventDefault();

    if (inFlight.current) return;

    // Cleared HERE, before either early-return check below — same reasoning
    // `ChangePasswordScreen.tsx` documents: without this, a server field
    // message from a PRIOR failed attempt stays under its field while a
    // fresh client-side block renders its own message above it.
    setFieldErrors({});
    setGeneralFailure(null);

    const trimmedEmail = email.trim();
    const trimmedCode = code.trim();

    const empty: FieldKey[] = [];
    if (trimmedEmail === '') empty.push('email');
    if (trimmedCode === '') empty.push('code');
    if (newPassword === '') empty.push('new');
    if (confirmPassword === '') empty.push('confirm');
    if (empty.length > 0) {
      setMissing(empty);
      // MUST-FIX 3 (whole-branch review): the field marks alone are
      // announced unreliably — they are born with their content, same as
      // `LoginPage.tsx`'s identical marks. The real alert region and moved
      // focus are what make this failure audible and actionable.
      setGeneralFailure(t('resetPassword.missingFields'));
      const first = empty[0];
      if (first !== undefined) fieldRefs[first].current?.focus();
      return;
    }
    setMissing([]);

    // Caught here so the person is told immediately, before either field
    // ever leaves the browser. The server checks it too, for real: `password`
    // and `confirmPassword` travel to `api.resetPassword` as two SEPARATE
    // arguments (see the module docblock and the client's own docblock), so
    // Laravel's `confirmed` rule on `ResetPasswordRequest` compares two
    // independently-transmitted values rather than one against itself.
    if (newPassword !== confirmPassword) {
      setGeneralFailure(t('resetPassword.mismatch'));
      return;
    }

    inFlight.current = true;
    setSubmitting(true);

    try {
      await api.resetPassword(trimmedEmail, trimmedCode, newPassword, confirmPassword);

      // Never left sitting in state once the API has confirmed the change —
      // the one moment every credential this screen held is guaranteed no
      // longer needed for anything, success or retry.
      setCode('');
      setNewPassword('');
      setConfirmPassword('');

      // See the module docblock's "R-2" section: no session, no user
      // resource — only the fact that it happened.
      onReset();
    } catch (error) {
      if (!(error instanceof ApiError)) {
        // A fault in our own code, not a refusal from the API. The agent
        // must be told the change did nothing, and the fault must not be
        // disguised as a credential problem.
        setGeneralFailure(t('resetPassword.failed'));
        throw error;
      }

      // Kept open, typing intact: nothing on this path touches the typed
      // fields. A failure here must not cost the person what they typed —
      // they have no way to know which field was wrong.
      //
      // The inner emptiness check matters on its own, same as
      // `ChangePasswordScreen.tsx`: `error.details` having KEYS is not the
      // same claim as `mapDetails` having produced any. A 422 whose
      // `details` carried some other key would fall through every entry in
      // `mapDetails`'s loop and leave `fieldErrors` at `{}` — without this
      // fallback, the person would see nothing at all after a submission
      // that visibly did nothing.
      if (Object.keys(error.details).length > 0) {
        const mapped = mapDetails(error.details);
        if (Object.keys(mapped).length > 0) {
          setFieldErrors(mapped);
        } else {
          setGeneralFailure(describeFailure(error));
        }
      } else {
        setGeneralFailure(describeFailure(error));
      }
    } finally {
      inFlight.current = false;
      setSubmitting(false);
    }
  }

  return (
    <main className={styles['page']} data-testid="resetPasswordPage">
      <form
        className={styles['card']}
        method="post"
        noValidate
        onSubmit={(event) => {
          void handleSubmit(event);
        }}
      >
        <h1 className={styles['title']}>{t('resetPassword.title')}</h1>
        <p className={styles['intro']}>{t('resetPassword.intro')}</p>

        {/*
          Mounted from the first frame, EMPTY — assistive technology
          announces a change inside a region it is already watching. Same
          shape as `ChangePasswordScreen.tsx` and `ForgotPasswordScreen.tsx`.
        */}
        <div className={styles['alert']} role="alert" aria-atomic="true">
          {generalFailure === null ? null : (
            <span className={styles['alertText']}>{generalFailure}</span>
          )}
        </div>

        <Input
          ref={emailRef}
          id="resetPasswordEmail"
          label={t('resetPassword.emailLabel')}
          width="fill"
          type="email"
          autoComplete="email"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          required
          value={email}
          error={missing.includes('email') ? t('form.requiredField') : fieldErrors.email}
          onChange={(event) => {
            setEmail(event.target.value);
          }}
        />

        {/*
          Stable, explicit ids on exactly the three security-sensitive
          fields — not `Field`'s auto-generated `useId()` default. The
          test-only DOM-leak check narrows to precisely these three ids
          (`KNOWN_SECRET_FIELD_IDS` in the test file), selecting by ID and
          NEVER by `type` — deliberately, so a regression that "fixed" the
          leak check by exempting non-`type="password"` fields could not
          silently stop covering this one. The code below is VISIBLE
          (`type="text"`) — see the module docblock's "The code is VISIBLE"
          section — and is exactly as much a secret as the two password
          fields beside it; only its type differs.
        */}
        <Input
          ref={codeRef}
          id="resetPasswordCode"
          label={t('resetPassword.codeLabel')}
          width="fill"
          type="text"
          autoComplete="one-time-code"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          required
          value={code}
          error={missing.includes('code') ? t('form.requiredField') : fieldErrors.code}
          onChange={(event) => {
            setCode(event.target.value);
          }}
        />

        <Input
          ref={newPasswordRef}
          id="resetPasswordNew"
          label={t('resetPassword.fieldNew')}
          width="fill"
          type="password"
          autoComplete="new-password"
          required
          value={newPassword}
          error={missing.includes('new') ? t('form.requiredField') : fieldErrors.new}
          onChange={(event) => {
            setNewPassword(event.target.value);
          }}
        />

        <Input
          ref={confirmPasswordRef}
          id="resetPasswordConfirm"
          label={t('resetPassword.fieldConfirm')}
          width="fill"
          type="password"
          autoComplete="new-password"
          required
          value={confirmPassword}
          error={missing.includes('confirm') ? t('form.requiredField') : fieldErrors.confirm}
          onChange={(event) => {
            setConfirmPassword(event.target.value);
          }}
        />

        <div className={styles['actions']}>
          <Button icon={<PasswordIcon size={16} />} type="submit" loading={submitting} className={styles['submit']}>
            {t('resetPassword.submit')}
          </Button>

          <Button icon={<ArrowLeftIcon size={16} className={styles['backIcon']} />} type="button" variant="ghost" onClick={onCancel}>
            {t('resetPassword.cancel')}
          </Button>
        </div>
      </form>
    </main>
  );
}
